// lectio エントリポイント
// ホーム画面のレンダリングと、各画面への遷移ルーティング
// CLAUDE.md S3-4: 動的データは escapeHtml を通す

import './style.css';
import { escapeHtml, fmtRelative, toast } from './ui.js';
import { getAllBooks, ls } from './cache.js';
import { GENRE_LIST } from './data/philosophers.js';
import { showBookDetailScreen } from './screens/book-detail.js';
import { showRecommendScreen } from './screens/recommend.js';
import { showSettingsScreen, syncNow } from './screens/settings.js';
import { showThoughtMapScreen } from './screens/thought-map.js';
import { showLineageScreen } from './screens/lineage.js';
import { showSerendipityScreen } from './screens/serendipity.js';
import { showContrastScreen } from './screens/contrast.js';
import { showJournalScreen } from './screens/journal.js';
import { showReadingCourseScreen } from './screens/reading-course.js';
import { showTimelineScreen } from './screens/timeline.js';
import { showWishlistScreen } from './screens/wishlist.js';
import { showKeywordSearchScreen } from './screens/keyword-search.js';
import { showHistoryScreen } from './screens/history.js';
import { showSemanticSearchScreen } from './screens/semantic-search.js';
import { showTbrScreen } from './screens/tbr.js';
import { showStatsScreen } from './screens/stats.js';
import { showRecentFocusScreen } from './screens/recent-focus.js';
import { showBookDialogScreen } from './screens/book-dialog.js';
import { showThoughtPatternsScreen } from './screens/thought-patterns.js';
import { showChatScreen } from './screens/chat.js';
import { showYearReportScreen } from './screens/year-report.js';
import { showVocabularyScreen } from './screens/vocabulary.js';
import { showStyleEvolutionScreen } from './screens/style-evolution.js';
import { showMemoLineageScreen } from './screens/memo-lineage.js';
import { maybeAutoBackup } from './drive.js';
import { fetchCover, illustrationPathFor, illustrationExists, fetchDailyPrompt, fetchShakyo, fetchMemoEcho, fetchSeasonalPick } from './api.js';
import { upsertBook, saveAiHistory, getAllAiHistory, getAiHistoryByType, getAllHighlights } from './cache.js';
import { fuzzyMatch } from './normalize.js';

const state = {
  books: [],
  activeGenre: 'すべて',
  searchKeyword: '',
  viewMode: localStorage.getItem('lectio.viewMode') || 'list', // 'list' | 'shelf'
};

window.addEventListener('DOMContentLoaded', init);

async function init() {
  initTheme();
  state.books = await getAllBooks();
  renderHero();
  renderGenreTabs();
  renderBookList();

  // ホームから他画面への遷移
  document.querySelectorAll('[data-screen]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.screen;
      route(target);
    });
  });

  document.getElementById('btn-settings').addEventListener('click', () => {
    history.pushState({ screen: 'settings' }, '', '#settings');
    showSettingsScreen();
  });

  document.getElementById('btn-theme').addEventListener('click', toggleTheme);
  document.getElementById('btn-view-toggle').addEventListener('click', toggleViewMode);
  updateViewToggleIcon();

  // ハンバーガードロワー開閉
  const drawer = document.getElementById('drawer');
  const openDrawer = () => {
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
  };
  const closeDrawer = () => {
    drawer.hidden = true;
    document.body.style.overflow = '';
  };
  document.getElementById('btn-menu').addEventListener('click', openDrawer);
  document.getElementById('btn-drawer-close').addEventListener('click', closeDrawer);
  document.getElementById('drawer-backdrop').addEventListener('click', closeDrawer);
  // ドロワー内のアクションをタップした時もドロワーを閉じる
  drawer.querySelectorAll('[data-screen]').forEach((btn) => {
    btn.addEventListener('click', closeDrawer);
  });
  document.getElementById('btn-sync').addEventListener('click', async () => {
    await syncNow();
    state.books = await getAllBooks();
    renderHero();
    renderBookList();
  });

  // 検索
  const searchBtn = document.getElementById('btn-search-books');
  const searchBar = document.getElementById('search-bar');
  const searchInput = document.getElementById('search-input');
  const searchClose = document.getElementById('btn-search-close');
  searchBtn.addEventListener('click', () => {
    searchBar.hidden = false;
    searchInput.focus();
  });
  searchClose.addEventListener('click', () => {
    searchInput.value = '';
    state.searchKeyword = '';
    searchBar.hidden = true;
    renderBookList();
  });
  searchInput.addEventListener('input', () => {
    state.searchKeyword = (searchInput.value || '').trim().toLowerCase();
    renderBookList();
  });

  // history.back() でホームに戻れるようにする
  window.addEventListener('popstate', () => {
    const screenMount = document.getElementById('screen-mount');
    if (screenMount.innerHTML.trim()) {
      // 何か画面が表示されている → ホームへ戻す
      document.getElementById('screen-home').classList.add('screen--active');
      screenMount.innerHTML = '';
    }
  });

  enableSwipeBack();
  checkSyncStaleness();
  loadDailyPrompt();
  loadShakyo();
  loadMemoEcho();
  loadSeasonalPick();
  renderWeeklyReport();
  renderTimeMachine();
  // 自動バックアップは設定時 + 30 日経過 + silent 認証成功の三条件で実行（UI ブロックなし）
  maybeAutoBackup().catch((e) => console.warn('auto backup error:', e?.message));
  registerServiceWorker();
}

// ============ 今日の問い / 今日の引用 ============
// 1 日 1 回 AI が選ぶ。localStorage に日付キー付きでキャッシュし、同日内は再呼び出ししない。
// 「リフレッシュ」ボタンを押すと再生成（キャッシュを上書き）。
async function loadDailyPrompt() {
  const card = document.getElementById('daily-prompt');
  if (!card) return;
  const today = new Date().toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });
  const cacheKey = `lectio.dailyPrompt.${today}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      paintDailyPrompt(JSON.parse(cached));
    } catch {}
  }

  document.getElementById('daily-prompt-refresh').addEventListener('click', () => {
    localStorage.removeItem(cacheKey);
    fetchAndPaintDailyPrompt(today, cacheKey, true);
  });
  document.getElementById('daily-prompt-card').addEventListener('click', () => {
    const bookId = card.dataset.bookId;
    if (bookId) {
      history.pushState({ screen: 'book-detail' }, '', `#book/${bookId}`);
      showBookDetailScreen(bookId);
    }
  });

  if (!cached) {
    fetchAndPaintDailyPrompt(today, cacheKey, false);
  }
}

async function fetchAndPaintDailyPrompt(date, cacheKey, force) {
  // メモ ありの既読/読書中書籍に絞って AI へ送る
  const candidates = state.books
    .filter((b) => (b.status === '読了' || b.status === '読書中') && (b.note || '').trim().length > 80)
    .map((b) => ({ id: b.id, name: b.name, author: b.author, note: b.note }));
  if (candidates.length < 2) return; // メモが少ないうちはサイレントに表示しない
  try {
    const res = await fetchDailyPrompt(candidates, date);
    if (!res?.text) return;
    localStorage.setItem(cacheKey, JSON.stringify(res));
    paintDailyPrompt(res);
    if (force) toast('別の本から引きました');
  } catch (e) {
    console.error('daily prompt failed:', e);
  }
}

function paintDailyPrompt(res) {
  const card = document.getElementById('daily-prompt');
  if (!card || !res?.text) return;
  card.hidden = false;
  card.dataset.bookId = res.bookId || '';
  document.getElementById('daily-prompt-mode').textContent =
    res.mode === 'quote' ? '今日の引用' : '今日の問い';
  document.getElementById('daily-prompt-text').textContent = res.text;
  const meta = [res.bookName, res.author].filter(Boolean).join(' / ');
  document.getElementById('daily-prompt-meta').textContent = meta;
}

// ============ 最終同期チェック ============
// 24時間以上 sync していない、または未同期の場合にバナーを表示。
// 「あとで」を押すと当日中は再表示しない（localStorage に dismissDate を保存）。
function checkSyncStaleness() {
  const banner = document.getElementById('sync-banner');
  if (!banner) return;
  const lastSync = ls.getLastSync();
  const STALE_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const todayKey = new Date(now).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });
  const dismissed = localStorage.getItem('lectio.syncBannerDismissed') === todayKey;

  let textEl = document.getElementById('sync-banner-text');
  let shouldShow = false;
  if (!lastSync) {
    if (textEl) textEl.textContent = 'まだ Notion と同期していません';
    shouldShow = true;
  } else if (now - lastSync > STALE_MS) {
    const days = Math.floor((now - lastSync) / STALE_MS);
    const label = days >= 1 ? `${days} 日前` : '24 時間以上前';
    if (textEl) textEl.textContent = `最終同期: ${label}。新しい記録を取り込めます`;
    shouldShow = true;
  }
  banner.hidden = !(shouldShow && !dismissed);

  const syncBtn = document.getElementById('btn-sync-banner');
  const closeBtn = document.getElementById('btn-sync-banner-close');
  if (syncBtn && !syncBtn.dataset.bound) {
    syncBtn.dataset.bound = '1';
    syncBtn.addEventListener('click', async () => {
      banner.hidden = true;
      await syncNow();
      state.books = await getAllBooks();
      renderHero();
      renderBookList();
    });
  }
  if (closeBtn && !closeBtn.dataset.bound) {
    closeBtn.dataset.bound = '1';
    closeBtn.addEventListener('click', () => {
      banner.hidden = true;
      localStorage.setItem('lectio.syncBannerDismissed', todayKey);
    });
  }
}

// ============ スワイプで戻る (iOS Safari ライク) ============
// 画面左端 (x < 24px) から開始した横スワイプが右に 80px 以上動いたら history.back()。
// 縦スクロールと誤判定しないよう、横移動量 > 縦移動量 × 1.5 を満たす場合のみ作動。
// ホーム画面（screen-mount が空）では戻れないので何もしない。
function enableSwipeBack() {
  const EDGE_PX = 24;
  const THRESHOLD_PX = 80;
  let startX = 0;
  let startY = 0;
  let startedAtEdge = false;

  window.addEventListener(
    'touchstart',
    (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      startedAtEdge = t.clientX < EDGE_PX;
      startX = t.clientX;
      startY = t.clientY;
    },
    { passive: true }
  );

  window.addEventListener(
    'touchend',
    (e) => {
      if (!startedAtEdge) return;
      startedAtEdge = false;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dx < THRESHOLD_PX) return;
      if (dx < dy * 1.5) return;
      // 何か画面が乗っていたら戻る
      const screenMount = document.getElementById('screen-mount');
      if (screenMount && screenMount.innerHTML.trim()) {
        history.back();
      }
    },
    { passive: true }
  );
}

function route(name) {
  // history を 1 つ進めて、ブラウザの戻るボタンでもホームに戻れるようにする
  history.pushState({ screen: name }, '', `#${name}`);
  switch (name) {
    case 'recommend':
      showRecommendScreen();
      break;
    case 'contrast':
      showContrastScreen();
      break;
    case 'serendipity':
      showSerendipityScreen();
      break;
    case 'thought-map':
      showThoughtMapScreen();
      break;
    case 'lineage':
      showLineageScreen();
      break;
    case 'reading-course':
      showReadingCourseScreen();
      break;
    case 'journal':
      showJournalScreen();
      break;
    case 'timeline':
      showTimelineScreen();
      break;
    case 'stats':
      showStatsScreen();
      break;
    case 'recent-focus':
      showRecentFocusScreen();
      break;
    case 'book-dialog':
      showBookDialogScreen();
      break;
    case 'thought-patterns':
      showThoughtPatternsScreen();
      break;
    case 'vocabulary':
      showVocabularyScreen();
      break;
    case 'style-evolution':
      showStyleEvolutionScreen();
      break;
    case 'memo-lineage':
      showMemoLineageScreen();
      break;
    case 'chat':
      showChatScreen();
      break;
    case 'year-report':
      showYearReportScreen();
      break;
    case 'tbr':
      showTbrScreen();
      break;
    case 'wishlist':
      showWishlistScreen();
      break;
    case 'keyword-search':
      showKeywordSearchScreen();
      break;
    case 'semantic-search':
      showSemanticSearchScreen();
      break;
    case 'history':
      showHistoryScreen();
      break;
    default:
      toast('未実装の画面です');
  }
}

function renderHero() {
  document.getElementById('meta-count').textContent = `${state.books.length} books`;
  document.getElementById('meta-sync').textContent = fmtRelative(ls.getLastSync());
}

function renderGenreTabs() {
  const tabs = document.getElementById('genre-tabs');
  const items = ['すべて', '読書中', ...GENRE_LIST];
  tabs.innerHTML = items
    .map(
      (g) =>
        `<button class="genre-tab${g === state.activeGenre ? ' genre-tab--active' : ''}" data-genre="${escapeHtml(g)}" type="button">${escapeHtml(g)}</button>`
    )
    .join('');
  tabs.querySelectorAll('[data-genre]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.activeGenre = btn.dataset.genre;
      renderGenreTabs();
      renderBookList();
    });
  });
}

function renderBookList() {
  const list = document.getElementById('book-list');
  const empty = document.getElementById('book-empty');
  let books = filterBooks(state.books, state.activeGenre, state.searchKeyword);

  if (!books.length) {
    list.innerHTML = '';
    list.className = 'book-list';
    empty.hidden = false;
    if (state.books.length && (state.activeGenre !== 'すべて' || state.searchKeyword)) {
      empty.textContent = '該当する書籍がありません。';
    } else {
      empty.textContent = 'まだ書籍がありません。設定から Notion を同期してください。';
    }
    return;
  }
  empty.hidden = true;

  // ステータス順（読書中 → 積読 → 読了）→ 更新降順
  books = books.sort((a, b) => {
    const statusRank = (s) => (s === '読書中' ? 0 : s === '積読' ? 1 : 2);
    const sa = statusRank(a.status);
    const sb = statusRank(b.status);
    if (sa !== sb) return sa - sb;
    return (b.updatedAt || '').localeCompare(a.updatedAt || '');
  });

  if (state.viewMode === 'shelf') {
    list.className = 'book-shelf';
    list.innerHTML = books.map(renderShelfTile).join('');
  } else {
    list.className = 'book-list';
    list.innerHTML = books.map(renderListItem).join('');
  }

  list.querySelectorAll('[data-book-id]').forEach((li) => {
    li.addEventListener('click', () => {
      history.pushState({ screen: 'book-detail' }, '', `#book/${li.dataset.bookId}`);
      showBookDetailScreen(li.dataset.bookId);
    });
  });

  // 全書籍について illustration 存在チェックと cover 取得を裏で実行
  // （ユーザー生成済みの data URL 書影を持つ本はサーバ書影も openBD も不要なので除外）
  lazyFetchCovers(books.filter((b) => !b.hasIllustration && !b.illustrationDataUrl));
}

function bookCoverSrc(b) {
  if (b.hasIllustration) return illustrationPathFor(b.id);
  if (b.illustrationDataUrl) return b.illustrationDataUrl;
  if (b.illustrationBlob) return URL.createObjectURL(b.illustrationBlob);
  if (b.coverUrl) return b.coverUrl;
  return null;
}

function renderListItem(b) {
  const initial = (b.name && b.name[0]) || '?';
  const coverSrc = bookCoverSrc(b);
  const coverHtml = coverSrc
    ? `<img class="book-cover" src="${escapeHtml(coverSrc)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
    : `<div class="book-cover">${escapeHtml(initial)}</div>`;
  const tags = (b.genre || []).map((g) => `<span class="book-meta__tag">${escapeHtml(g)}</span>`).join('');
  const statusCls = b.status === '読書中' ? ' book-meta__status--reading' : '';
  const rating = b.rating ? `<span class="book-meta__tag">${escapeHtml(b.rating)}</span>` : '';
  return `
    <li class="book-item" data-book-id="${escapeHtml(b.id)}">
      ${coverHtml}
      <div class="book-body">
        <p class="book-title">${escapeHtml(b.name)}</p>
        <p class="book-author">${escapeHtml(b.author || '著者未登録')}</p>
        <div class="book-meta">
          ${b.status ? `<span class="book-meta__tag${statusCls}">${escapeHtml(b.status)}</span>` : ''}
          ${rating}
          ${tags}
        </div>
      </div>
    </li>
  `;
}

function renderShelfTile(b) {
  const initial = (b.name && b.name[0]) || '?';
  const coverSrc = bookCoverSrc(b);
  const coverHtml = coverSrc
    ? `<img class="shelf-tile__cover" src="${escapeHtml(coverSrc)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
    : `<div class="shelf-tile__cover shelf-tile__cover--fallback">${escapeHtml(initial)}</div>`;
  const statusBadge = b.status === '読書中'
    ? '<span class="shelf-tile__status shelf-tile__status--reading">読書中</span>'
    : '';
  return `
    <li class="shelf-tile" data-book-id="${escapeHtml(b.id)}">
      <div class="shelf-tile__frame">
        ${coverHtml}
        ${statusBadge}
      </div>
      <p class="shelf-tile__title">${escapeHtml(b.name)}</p>
      <p class="shelf-tile__author">${escapeHtml(b.author || '')}</p>
    </li>
  `;
}

function toggleViewMode() {
  state.viewMode = state.viewMode === 'list' ? 'shelf' : 'list';
  localStorage.setItem('lectio.viewMode', state.viewMode);
  updateViewToggleIcon();
  renderBookList();
}

function updateViewToggleIcon() {
  const use = document.querySelector('#btn-view-toggle use');
  if (!use) return;
  // shelf 表示中はリストアイコン、list 表示中は grid アイコン
  use.setAttribute('href', state.viewMode === 'shelf' ? '#i-list' : '#i-grid');
}

function filterBooks(books, genre, keyword) {
  // ホーム library は積読を表示しない（積読は「積読リスト」画面で管理）
  let out = books.filter((b) => b.status !== '積読');
  if (genre && genre !== 'すべて') {
    if (genre === '読書中') {
      out = out.filter((b) => b.status === '読書中');
    } else {
      out = out.filter((b) => (b.genre || []).includes(genre));
    }
  }
  if (keyword) {
    // あいまい一致: NFKC + カタカナ→ひらがな + lower + 空白AND
    out = out.filter((b) => {
      const haystack = `${b.name || ''} ${b.author || ''} ${(b.genre || []).join(' ')}`;
      return fuzzyMatch(haystack, keyword);
    });
  }
  return out;
}

async function lazyFetchCovers(books) {
  const concurrency = 3;
  const queue = [...books];
  const workers = Array.from({ length: concurrency }, async () => {
    while (queue.length) {
      const b = queue.shift();
      if (!b) break;

      // 1. 事前生成された AI イラストがあれば最優先（装丁参考の統一トーン）
      if (!b.hasIllustration) {
        const exists = await illustrationExists(b.id);
        if (exists) {
          b.hasIllustration = true;
          await upsertBook(b);
          swapCover(b.id, illustrationPathFor(b.id));
          continue;
        }
      } else {
        swapCover(b.id, illustrationPathFor(b.id));
        continue;
      }

      // 2. AI イラストが無ければ openBD のリアル書影を試す
      if (!b.coverUrl) {
        try {
          const { coverUrl } = await fetchCover(b.name, b.author);
          if (coverUrl) {
            b.coverUrl = coverUrl;
            await upsertBook(b);
            swapCover(b.id, coverUrl);
          }
        } catch {}
      }
    }
  });
  await Promise.all(workers);
}

function swapCover(bookId, url) {
  const li = document.querySelector(`[data-book-id="${cssEscape(bookId)}"]`);
  if (!li) return;
  const cover = li.querySelector('.book-cover');
  if (!cover) return;
  const img = document.createElement('img');
  img.className = 'book-cover';
  img.src = url;
  img.alt = '';
  img.referrerPolicy = 'no-referrer';
  img.loading = 'lazy';
  cover.replaceWith(img);
}

function cssEscape(s) {
  if (window.CSS && CSS.escape) return CSS.escape(s);
  return String(s).replace(/[^a-zA-Z0-9-_]/g, '\\$&');
}

// ============ 写経の儀式 ============
// 既読本のメモから AI が「今夜手書きで写すべき一節」を選んでホーム上に表示。
// 一度生成したら 3 日間はキャッシュ。手書きで写したらユーザーが「写経済」をチェック。
async function loadShakyo() {
  const card = document.getElementById('shakyo');
  if (!card) return;
  const cacheKey = 'lectio.shakyo';
  const CACHE_MS = 3 * 24 * 60 * 60 * 1000;
  let data = null;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && obj.ts && Date.now() - obj.ts < CACHE_MS) data = obj.data;
    }
  } catch {}

  if (!data) {
    const candidates = state.books
      .filter((b) => (b.status === '読了' || b.status === '読書中') && (b.note || '').trim().length > 100)
      .map((b) => ({ id: b.id, name: b.name, author: b.author, note: b.note }));
    if (candidates.length < 2) return;
    try {
      data = await fetchShakyo(candidates);
      if (!data?.passage) return;
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
      console.warn('shakyo fetch failed:', e?.message);
      return;
    }
  }

  card.hidden = false;
  document.getElementById('shakyo-passage').textContent = data.passage || '';
  const meta = [data.bookName, data.author].filter(Boolean).join(' / ');
  document.getElementById('shakyo-meta').textContent = meta;
  document.getElementById('shakyo-reason').textContent = data.reason || '';

  document.getElementById('shakyo-done-btn').addEventListener('click', async () => {
    await saveAiHistory({
      type: 'shakyo',
      title: `写経: ${data.bookName || ''}`,
      subtitle: (data.passage || '').slice(0, 40),
      context: { bookId: data.bookId, bookName: data.bookName, passage: data.passage },
      payload: data,
    });
    localStorage.removeItem(cacheKey);
    card.hidden = true;
    toast('写経済として記録しました');
  });
}

// ============ 過去メモのランダム再会 ============
async function loadMemoEcho() {
  const card = document.getElementById('memo-echo');
  if (!card) return;
  const cacheKey = 'lectio.memoEcho';
  const CACHE_MS = 7 * 24 * 60 * 60 * 1000; // 週に一度入れ替わる
  let data = null;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && obj.ts && Date.now() - obj.ts < CACHE_MS) data = obj.data;
    }
  } catch {}

  if (!data) {
    const candidates = state.books
      .filter((b) => (b.note || '').trim().length > 200)
      .map((b) => ({ id: b.id, name: b.name, note: b.note }));
    if (candidates.length < 3) return;
    try {
      data = await fetchMemoEcho(candidates);
      if (!data?.excerpt) return;
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
      console.warn('memo-echo fetch failed:', e?.message);
      return;
    }
  }

  card.hidden = false;
  document.getElementById('memo-echo-text').textContent = `「${data.excerpt}」`;
  document.getElementById('memo-echo-meta').textContent = data.bookName || '';
  document.getElementById('memo-echo-prompt').textContent = data.prompt || '';
  card.dataset.bookId = data.bookId || '';
  document.getElementById('memo-echo-btn').addEventListener('click', () => {
    if (data.bookId) {
      history.pushState({ screen: 'book-detail' }, '', `#book/${data.bookId}`);
      showBookDetailScreen(data.bookId);
    }
  });
}

// ============ 季節・時節の一冊 ============
async function loadSeasonalPick() {
  const card = document.getElementById('seasonal-pick');
  if (!card) return;
  const cacheKey = 'lectio.seasonalPick';
  const CACHE_MS = 24 * 60 * 60 * 1000;
  let data = null;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && obj.ts && Date.now() - obj.ts < CACHE_MS) data = obj.data;
    }
  } catch {}

  if (!data) {
    if (state.books.length < 5) return;
    const now = new Date();
    const m = now.getMonth() + 1;
    const h = now.getHours();
    const season =
      m === 12 || m <= 2 ? '冬' :
      m <= 5 ? '春' :
      m <= 8 ? '夏' : '秋';
    const time =
      h < 5 ? '深夜' :
      h < 10 ? '朝' :
      h < 16 ? '昼' :
      h < 19 ? '夕' :
      h < 23 ? '夜' : '深夜';
    const dateLabel = now.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo', month: 'long', day: 'numeric' });
    const eligible = state.books
      .filter((b) => b.status === '読書中' || b.status === '読了' || b.status === '積読')
      .map((b) => ({ id: b.id, name: b.name, author: b.author, genre: b.genre, status: b.status, note: b.note }));
    if (eligible.length < 5) return;
    try {
      data = await fetchSeasonalPick(eligible, season, time, dateLabel);
      if (!data?.bookId) return;
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
      console.warn('seasonal pick failed:', e?.message);
      return;
    }
  }

  card.hidden = false;
  document.getElementById('seasonal-pick-title').textContent = data.bookName || '';
  document.getElementById('seasonal-pick-meta').textContent = data.author || '';
  document.getElementById('seasonal-pick-mood').textContent = data.mood || '';
  card.dataset.bookId = data.bookId || '';
  document.getElementById('seasonal-pick-btn').addEventListener('click', () => {
    if (data.bookId) {
      history.pushState({ screen: 'book-detail' }, '', `#book/${data.bookId}`);
      showBookDetailScreen(data.bookId);
    }
  });
}

// ============ 週報（先週のlectio） ============
async function renderWeeklyReport() {
  const card = document.getElementById('weekly-report');
  if (!card) return;
  const body = document.getElementById('weekly-report-body');
  const now = Date.now();
  const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
  const histories = await getAllAiHistory();
  const recent = histories.filter((h) => (h.createdAt || 0) >= weekAgo);
  const finishedLastWeek = state.books.filter((b) => {
    if (b.status !== '読了' || !b.endDate) return false;
    const d = new Date(b.endDate).getTime();
    return d >= weekAgo;
  }).length;
  const shakyoCount = recent.filter((h) => h.type === 'shakyo').length;
  const aiCount = recent.length - shakyoCount;
  const allHighlights = await getAllHighlights();
  const newHighlights = allHighlights.filter((h) => (h.createdAt || 0) >= weekAgo).length;

  if (recent.length === 0 && finishedLastWeek === 0 && newHighlights === 0) {
    card.hidden = true;
    return;
  }
  card.hidden = false;

  // 先週 AI 機能の最も多用したタイプ
  const typeCount = new Map();
  for (const h of recent) typeCount.set(h.type, (typeCount.get(h.type) || 0) + 1);
  const topType = [...typeCount.entries()].sort((a, b) => b[1] - a[1])[0];
  const typeJa = {
    'recommend': 'おすすめ', 'keyword-search': 'キーワード探索', 'contrast': '対立軸',
    'serendipity': 'セレンディピティ', 'map': '思想マップ', 'lineage': '系譜',
    'course': '読書合宿', 'journal': '月次ジャーナル', 'analyze': '哲学者分析',
    'ask': '深掘り対話', 'tbr-priority': '積読優先度', 'next-question': '次の問い',
    'recent-focus': '最近の関心', 'book-dialog': '書籍間対話', 'thought-patterns': '思考の癖',
    'chat': 'AI 対話', 'year-report': '年報', 'book-chat': '本人 AI',
    'shakyo': '写経', 'pre-reading-reflection': '読書前の問い',
  };
  const parts = [];
  if (finishedLastWeek) parts.push(`読了 <strong>${finishedLastWeek}</strong> 冊`);
  if (aiCount) parts.push(`AI 機能 <strong>${aiCount}</strong> 回`);
  if (shakyoCount) parts.push(`写経 <strong>${shakyoCount}</strong> 件`);
  if (newHighlights) parts.push(`ハイライト <strong>${newHighlights}</strong>`);
  let html = parts.join(' · ');
  if (topType) html += `<br/>最も使った機能: <strong>${typeJa[topType[0]] || topType[0]}</strong>`;
  body.innerHTML = html;
}

// ============ N 年前のメモスポットライト ============
// endDate が今日と同月同日（過去 1/2/3/5/10 年前）の書籍をホーム下部に表示。
// 該当書籍がある年だけ表示し、無ければ section ごと隠す。AI 呼び出しゼロ。
function renderTimeMachine() {
  const section = document.getElementById('time-machine');
  const list = document.getElementById('time-machine-list');
  if (!section || !list) return;
  const today = new Date();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();
  const YEARS_AGO = [1, 2, 3, 5, 10];
  const hits = [];
  for (const y of YEARS_AGO) {
    const yearAgo = today.getFullYear() - y;
    for (const b of state.books) {
      if (!b.endDate) continue;
      const d = new Date(b.endDate);
      if (isNaN(d)) continue;
      if (d.getFullYear() === yearAgo && d.getMonth() === todayMonth && d.getDate() === todayDate) {
        hits.push({ book: b, yearsAgo: y });
      }
    }
  }
  if (hits.length === 0) {
    section.hidden = true;
    return;
  }
  section.hidden = false;
  list.innerHTML = hits
    .map(({ book, yearsAgo }) => {
      const coverSrc = book.hasIllustration
        ? illustrationPathFor(book.id)
        : book.illustrationDataUrl
        ? book.illustrationDataUrl
        : book.illustrationBlob
        ? URL.createObjectURL(book.illustrationBlob)
        : book.coverUrl;
      const coverHtml = coverSrc
        ? `<img class="time-machine__cover" src="${escapeHtml(coverSrc)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
        : `<div class="time-machine__cover time-machine__cover--fallback">${escapeHtml(book.name?.[0] || '?')}</div>`;
      return `
        <li class="time-machine__item" data-book-id="${escapeHtml(book.id)}">
          ${coverHtml}
          <div class="time-machine__body">
            <p class="time-machine__years">${yearsAgo} 年前の今日</p>
            <p class="time-machine__title">${escapeHtml(book.name)}</p>
            <p class="time-machine__author">${escapeHtml(book.author || '')}</p>
          </div>
        </li>
      `;
    })
    .join('');
  list.querySelectorAll('[data-book-id]').forEach((li) => {
    li.addEventListener('click', () => {
      history.pushState({ screen: 'book-detail' }, '', `#book/${li.dataset.bookId}`);
      showBookDetailScreen(li.dataset.bookId);
    });
  });
}

// ============ Theme (light / dark) ============
// localStorage に値があればそれを優先、無ければ OS 設定（@media prefers-color-scheme）に追従
function initTheme() {
  const saved = localStorage.getItem('lectio.theme');
  if (saved === 'dark' || saved === 'light') {
    document.documentElement.dataset.theme = saved;
  }
  updateThemeIcon();
  updateMetaThemeColor();
}

function currentTheme() {
  const explicit = document.documentElement.dataset.theme;
  if (explicit === 'dark' || explicit === 'light') return explicit;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function toggleTheme() {
  const next = currentTheme() === 'dark' ? 'light' : 'dark';
  document.documentElement.dataset.theme = next;
  localStorage.setItem('lectio.theme', next);
  updateThemeIcon();
  updateMetaThemeColor();
}

function updateThemeIcon() {
  const use = document.querySelector('#btn-theme use');
  if (!use) return;
  // ダーク表示中は太陽（ライトへ戻すアイコン）、ライト表示中は月（ダークへのアイコン）
  use.setAttribute('href', currentTheme() === 'dark' ? '#i-sun' : '#i-moon');
}

function updateMetaThemeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  meta.setAttribute('content', currentTheme() === 'dark' ? '#0a0a0a' : '#ffffff');
}

// OS テーマ変更時、localStorage に保存が無い場合のみ追従
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (!localStorage.getItem('lectio.theme')) {
    updateThemeIcon();
    updateMetaThemeColor();
  }
});

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((e) => {
        console.error('SW registration failed:', e);
      });
    });
  } else {
    // 開発時は既存のSWを全て解除（他プロジェクトとの混在を防ぐ）
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
  }
}
